/**
 * pagedSelect.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Page a PostgREST read to completion.
 *
 * Why this exists
 * ---------------
 * PostgREST caps every response at the project's `max-rows` setting.
 * A `.select()` with no `.range()` returns at most that many rows and
 * reports **no error** — the remainder is silently dropped. For this
 * app that is a correctness bug, not a perf nit: a truncated
 * `ownership_records` read makes an interest estimate short, and a
 * truncated `deal_activities` read makes deal history disappear.
 *
 * `fetchAllRows` walks pages until a short one comes back.
 *
 * Two contracts callers must honor:
 *
 *   1. `buildQuery` is a **thunk**. A PostgREST builder is single-use;
 *      each page needs a freshly built one.
 *
 *   2. The query must carry a **total order** — a business column plus
 *      a unique tiebreaker (`id`). Under a non-unique sort key,
 *      Postgres may order ties differently between the two queries
 *      backing consecutive pages, so a row can appear on both pages
 *      while another is never returned at all.
 */

export const READ_PAGE_SIZE = 1000;

/**
 * @param {string} label            Used in the error message.
 * @param {() => object} buildQuery Thunk returning a fresh PostgREST builder.
 * @param {object}   [opts]
 * @param {number}   [opts.pageSize]  Rows per request.
 * @param {number}   [opts.maxRows]   Hard stop so a pathological table
 *                                    can't pull unbounded memory.
 * @param {(err: any, label: string) => void} [opts.onError]
 *        Called on a query error. Defaults to throwing.
 * @returns {Promise<any[]>} Every matching row, in query order.
 */
export async function fetchAllRows(label, buildQuery, opts = {}) {
  const {
    pageSize = READ_PAGE_SIZE,
    maxRows = Infinity,
    onError = (err) => {
      throw new Error(`${label}: ${err?.message || 'Unknown error'}`);
    },
  } = opts;

  if (!(pageSize > 0)) throw new Error(`${label}: pageSize must be positive`);

  const all = [];
  let from = 0;

  for (;;) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) {
      onError(error, label);
      return all;
    }
    if (!data || data.length === 0) break;

    all.push(...data);

    // A short page means we reached the end. This is the only reliable
    // signal — PostgREST does not tell us the total without an extra
    // count request.
    if (data.length < pageSize) break;

    if (all.length >= maxRows) {
      console.warn(`[pagedSelect] ${label}: stopped at ${all.length} rows (maxRows cap)`);
      break;
    }
    from += pageSize;
  }

  return all;
}
